// state managed by vue-stash (simple state management)
// @see https://github.com/cklmercer/vue-stash

import { languages, ILesson, IUser, ITranslation, ITestResult } from '@/models'
import {default as LocalForage} from 'localforage'
import { hashCode } from '@/utils'
// import firebase from 'firebase/app'
// import 'firebase/firestore'
import Firestore from '@/firestore'

const localForage = LocalForage.createInstance({ name: 'vocab-webui' })

const isDev = process.env && process.env['NODE_ENV'] === 'development'

// // our application state (shared to all components thanks to vue-stash)
// export const store = {
//   initialized: false,
//   initError: undefined,

//   isDev,
//   user: null as IUser | null,
//   languages: languages,

//   lessons: null as ILesson[] | null,
//   testResultsByLesson: null as Map<string, ITestResult[]> | null,

//   init,
//   setUser,
//   saveUser,
//   listUsers,
//   logout,
//   createLesson,
//   deleteLesson,
//   getLesson,
//   saveLesson,
//   saveLessonWord,
//   saveLessonWords,
//   deleteLessonWord,
//   saveTestResult,
//   loadTestResults,
//   getTestResults
// }

class Store {
  private _users = null as Promise<Map<string, IUser>> | null

  initialized = false
  initError = undefined
  readonly isDev = isDev

  user = null as IUser | null
  languages = languages

  lessons = null as ILesson[] | null
  testResultsByLesson = null as Map<string, ITestResult[]> | null

  get users(): Promise<Map<string, IUser>> | null {
    if (! this._users) {
      this._users = Firestore.Users.list().then(users => {
        const map = new Map<string, IUser>()
        users.forEach(_ => map.set(_.email, _))
        return map
      })
    }
    return this._users
  }

  init = init
  setUser = setUser
  saveUser = saveUser
  listUsers= listUsers
  logout = logout
  createLesson = createLesson
  deleteLesson = deleteLesson
  getLesson = getLesson
  saveLesson = saveLesson
  saveLessonWord = saveLessonWord
  saveLessonWords = saveLessonWords
  deleteLessonWord = deleteLessonWord
  getTestResult = getTestResult
  saveTestResult = saveTestResult
  getTestResults = getTestResults
  getMyTestResults = getMyTestResults
  deleteResult = deleteResult
}
// our application state (shared to all components thanks to vue-stash)
export const store = new Store()

export type StoreType = typeof store

// class LessonsDB {
//   constructor(protected db: LocalForage) {}

//   async getLessons() {
//     const lessonsKeys = (await this.db.keys()).filter(_ => _.startsWith('lesson_'))
//     const tmp = await Promise.all(lessonsKeys.map(_ => this.db.getItem(_) as Promise<null | ILesson>))
//     return tmp.filter(_ => _ != null) as ILesson[]
//   }

//   async createLesson(lesson: ILesson) {
//     const id = `lesson_${lesson._id}`
//     // lessons must be unique
//     if (await this.db.getItem(id) != null)
//       throw new Error(`Lesson with id ${id} already exists`)
//     // lessons names must be unique
//     if (await this.findByName(lesson.name))
//       throw new Error(`Lesson with name ${lesson.name} already exists`)

//     console.debug('[LessonsDB] createLesson', lesson)
//     return await this.db.setItem(id, lesson)
//   }

//   async deleteLesson(lesson: ILesson) {
//     const id = `lesson_${lesson._id}`
//     if (! await this.db.getItem(id))
//       throw new Error(`Delete failed: lesson id=${id} does not exist`)
//     return this.db.removeItem(id)
//   }

//   async findByName(lessonName: string) {
//     const lessons = await this.getLessons()
//     return lessons.find(_ => _.name === lessonName)
//   }

//   async saveLesson(lesson: ILesson) {
//     const id = `lesson_${lesson._id}`
//     if (! await this.db.getItem(id))
//       throw new Error(`Save failed: lesson id=${id} does not exist`)
//     return this.db.setItem(id, lesson)
//   }

//   private lessonEquals(left: ILesson, right: ILesson): boolean {
//     return left.name == right.name && left.created == right.created
//   }
// }

// let lessonsDB!: LessonsDB

let initPromise: Promise<any> | undefined = undefined

async function init() {
  console.debug('[store] init()')
  console.time('[store] init perf')
//lessonsDB = new LessonsDB(localForage)

  // create a Promise we can wait for to be sure the store is initialized
  initPromise = new Promise(async (resolve, reject) => {
    try {
      await Firestore.init()
    } catch (error) {
      console.error('[store] Firestore initialization error: ', error)
      store.initError = error
      reject(error)
      return
    }
  
    console.debug('[store] init - store.user = ' + store.user)
    if (store.user) {
      try {
        console.debug('[store] getting lessons...')
        store.lessons = await getLessons()
        console.debug('[store] lessons', store.lessons)
        
        // const testResults = await Firestore.TestResults.listByUser(store.user.uid)
        // const testResultsByLesson = new Map<string, ITestResult[]>()
        // if (testResults) {
        //   testResults.forEach(testResult => {
        //     const array = testResultsByLesson.get(testResult.lesson._id)
        //     if (array) array.push(testResult)
        //     else testResultsByLesson.set(testResult.lesson._id, [testResult])
        //   })
        // }
        // store.testResultsByLesson = testResultsByLesson
        // console.debug('[store] testResultsByLesson', testResultsByLesson)
      } catch (error) {
        store.initError = error
        reject(error)
        return
      }
    }

    console.log('[store]  init finished!')
    store.initialized = true
    resolve()
  }).finally(() => console.timeEnd('[store] init perf'))
}

function setUser(user: IUser | null) {
  console.debug('[store] setUser', user)
  store.user = user
}

async function saveUser(user: IUser) {
  return Firestore.Users.add(user)
}

async function listUsers() { return Firestore.Users.list() }

function logout() {
  return Firestore.logout()
}

function getLessons() {
  return Firestore.Lessons.getLessons()
}

async function createLesson(lesson: ILesson) {
  console.debug('[store] createLesson', lesson)
  await Firestore.Lessons.create(lesson)
  store.lessons!.push(lesson)
  return lesson
}

async function deleteLesson(lesson: ILesson) {
  console.debug('[store] deleteLesson', lesson)
  const index = store.lessons!.findIndex(_ => _._id === lesson._id)
  if (index == -1) throw new Error(`Cannot find lesson id=${lesson._id} in store !`)
  await Firestore.Lessons.deleteLesson(lesson._id)
  store.lessons!.splice(index, 1)
  return true
}

async function getLesson(id: string): Promise<ILesson | undefined> {
  await initPromise
  if (store.lessons) {
    console.debug(`getLesson(${id})`)
    return store.lessons.find(_ => _._id === id)
  } else
    return undefined
}

async function saveLesson(lesson: ILesson) {
  return Firestore.Lessons.save(lesson)
}

async function saveLessonWord(lesson: ILesson, word: ITranslation) {
  return Firestore.Lessons.saveWord(lesson._id, word)
}

async function saveLessonWords(lesson: ILesson, words: ITranslation[]) {
  return Firestore.Lessons.saveWords(lesson._id, words)
}

async function deleteLessonWord(lesson: ILesson, word: ITranslation) {
  return Firestore.Lessons.deleteWord(lesson._id, word)
}

async function getTestResult(id: string) {
  return Firestore.TestResults.get(id)
}

async function saveTestResult(testResult: ITestResult) {
  return Firestore.TestResults.save(testResult)
}

async function getMyTestResults() {
  if (store.user) {
    return Firestore.TestResults.listByUser(store.user.uid)
  } else return null
}

async function getTestResults(descending: boolean = false) {
  return Firestore.TestResults.list(descending)
}

async function deleteResult(id: string) {
  return Firestore.TestResults.deleteResult(id)
}